/* Case Files — the public gallery of closed city mysteries. Every solved (or cold)
   case across every world: the briefing, the evidence, the culprit, and the
   detective who cracked it. Open cases never leak here (backend enforces). */
import { useEffect, useState } from "react";
import { FileSearch, Gavel, MapPin, UserRound, CalendarDays, Snowflake, Trophy, Medal } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";

function CaseFile({ c }) {
  const solved = c.status === "solved";
  return (
    <article data-testid={`case-file-${c.id}`}
      className={`relative border bg-black/40 p-6 flex flex-col gap-4 overflow-hidden ${solved ? "border-amber-500/25" : "border-white/10"}`}>
      {/* stamp */}
      <span className={`absolute top-4 right-4 font-mono text-[9px] font-bold uppercase tracking-[0.3em] border-2 px-2.5 py-1 rotate-6 ${solved ? "border-amber-500/60 text-amber-400" : "border-sky-500/50 text-sky-400"}`}>
        {solved ? "Case closed" : "Gone cold"}
      </span>

      <div>
        <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
          <MapPin className="w-3 h-3" /> {c.server_name} · <CalendarDays className="w-3 h-3" /> {new Date(c.created_at).toLocaleDateString()}
        </p>
        <h3 className="font-sans font-bold uppercase tracking-wide text-lg text-zinc-50 mt-1.5 max-w-[80%]">{c.title}</h3>
      </div>

      <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">{c.briefing}</p>

      <div className="flex flex-col gap-1.5">
        {c.evidence.map((e, i) => (
          <p key={i} data-testid="case-evidence" className="font-mono text-[9px] text-zinc-500 flex gap-2">
            <span className="text-amber-500/70 uppercase tracking-[0.1em] shrink-0">[{e.kind}]</span>
            <span>{e.label} — {e.detail}</span>
          </p>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {c.suspects.map((s) => (
          <span key={s.name} className={`font-mono text-[8px] uppercase tracking-[0.12em] border px-2 py-1 ${s.name === c.culprit ? "border-red-500/50 text-red-400 bg-red-500/[0.06]" : "border-white/10 text-zinc-500"}`}>
            {s.name}{s.name === c.culprit ? " — guilty" : ""}
          </span>
        ))}
      </div>

      <div className={`border-t pt-3.5 mt-auto ${solved ? "border-amber-500/20" : "border-white/10"}`}>
        {solved ? (
          <p className="font-mono text-[10px] text-zinc-300 leading-relaxed flex items-start gap-2">
            <Gavel className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
            <span><span className="text-amber-300 font-bold">{c.culprit}</span> — {c.motive}.
              <span className="block text-zinc-500 text-[9px] mt-1 flex items-center gap-1">
                <UserRound className="w-3 h-3" /> cracked by <span className="text-zinc-200 font-bold">{c.solved_by}</span> · ${c.reward?.toLocaleString()} bounty
              </span>
            </span>
          </p>
        ) : (
          <p className="font-mono text-[10px] text-zinc-500 flex items-center gap-2">
            <Snowflake className="w-3.5 h-3.5 text-sky-400" /> The trail went cold. It was <span className="text-zinc-300 font-bold">{c.culprit}</span> — nobody called it.
          </p>
        )}
      </div>
    </article>
  );
}

const MEDALS = ["text-amber-400 border-amber-500/50 bg-amber-500/[0.08]", "text-zinc-300 border-zinc-400/40 bg-zinc-400/[0.06]", "text-orange-400 border-orange-500/40 bg-orange-500/[0.06]"];

function DetectiveBoard({ rows }) {
  if (!rows?.length) return null;
  return (
    <section data-testid="detective-board" className="mt-12 border border-white/10 bg-black/40 p-6 sm:p-8">
      <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-amber-500 flex items-center gap-2">
        <Trophy className="w-4 h-4" /> Hall of Detectives
      </p>
      <p className="font-mono text-[11px] text-zinc-400 mt-2 leading-relaxed">
        The citizens who close cases. Bounties stack, reputations follow you across every world.
      </p>
      <div className="mt-6 flex flex-col gap-2">
        {rows.map((r, i) => (
          <div key={r.detective} data-testid={`detective-row-${i}`}
            className={`flex items-center gap-4 border px-4 py-3.5 flex-wrap ${i < 3 ? MEDALS[i] : "border-white/[0.07] bg-white/[0.02]"}`}>
            <span className="font-sans font-bold text-lg w-8 text-center">{i + 1}</span>
            {i < 3 ? <Medal className="w-4 h-4 shrink-0" /> : <UserRound className="w-4 h-4 text-zinc-600 shrink-0" />}
            <p className="font-mono text-xs font-bold text-zinc-50 uppercase tracking-[0.12em] flex-1 min-w-32 truncate">{r.detective}</p>
            <span className="font-mono text-[10px] text-zinc-400">{r.cases} {r.cases === 1 ? "case" : "cases"} closed</span>
            <span className="font-mono text-[10px] text-emerald-400 font-bold">${r.bounties?.toLocaleString()} earned</span>
            <span className="font-mono text-[9px] text-zinc-600">{new Date(r.last_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function CaseFilesPage() {
  const [cases, setCases] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    api.get("/nc/mysteries/gallery").then((r) => { setCases(r.data.cases); setLeaderboard(r.data.leaderboard || []); }).catch(() => setCases([]));
  }, []);

  return (
    <div data-testid="case-files-page" className="min-h-screen bg-[#05070a] text-zinc-100">
      <SiteNav />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-amber-500 flex items-center gap-2">
          <FileSearch className="w-4 h-4" /> Bornwood Forensics — public record
        </p>
        <h1 className="font-bold uppercase tracking-wide text-2xl sm:text-4xl mt-3 max-w-3xl leading-tight">Case Files</h1>
        <p className="font-mono text-xs text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          Every closed mystery from every world — the evidence, the culprit, and the citizen who named them.
          Open cases stay sealed until someone cracks them in-game.
        </p>

        {cases === null && <p className="font-mono text-xs text-zinc-500 mt-12 animate-pulse">Opening the filing cabinet…</p>}
        <DetectiveBoard rows={leaderboard} />
        {cases?.length === 0 && (
          <div className="border border-dashed border-white/15 p-12 text-center mt-12">
            <FileSearch className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="font-mono text-xs text-zinc-400 mt-4">No closed cases yet — the city's first whodunit is still out there.</p>
            <p className="font-mono text-[10px] text-zinc-600 mt-2">Server owners generate weekly cold cases from the Owner Deck → Bulletins → City Levers.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
          {(cases || []).map((c) => <CaseFile key={c.id} c={c} />)}
        </div>
      </div>
    </div>
  );
}
